import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import SuggestionsBannerImage from '../assets/suggestions.png';
import './Suggestions.css';


// ─── CONFIG ────────────────────────────────────────────────────────────────────
const BOARD_ID = '5094921100';
const API_TOKEN      = process.env.REACT_APP_MONDAY_TOKEN;
const DESC_COLUMN_ID = 'text_mm2ksnpm'; // "Describe it." column

const MONDAY_URL     = 'https://api.monday.com/v2';
const MONDAY_HEADERS = {
  'Content-Type': 'application/json',
  Authorization:  `Bearer ${API_TOKEN}`,
  'API-Version':  '2024-01',
};


// ─── CREATE ITEM ──────────────────────────────────────────────────────────────
// column_values must be passed as a JSON string literal inside the mutation —
// NOT as a GraphQL variable. This is the Monday-documented approach.
async function createItem(name, description) {
  // Build the column_values JSON string, safely escaping the description
  const colVals = JSON.stringify({ [DESC_COLUMN_ID]: description });

  // Escape the item name for safe embedding in the GraphQL string
  const safeName = name.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  const mutation = `
    mutation {
      create_item(
        board_id: ${BOARD_ID},
        item_name: "${safeName}",
        column_values: ${JSON.stringify(colVals)}
      ) {
        id
        name
      }
    }
  `;

  const res = await fetch(MONDAY_URL, {
    method: 'POST',
    headers: MONDAY_HEADERS,
    body: JSON.stringify({ query: mutation }),
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  console.log('[Suggestions] create_item response:', JSON.stringify(json, null, 2));
  if (json.errors?.length) throw new Error(json.errors[0].message);
  if (!json?.data?.create_item?.id) throw new Error('Item was not created — check board ID and token.');
  return json.data.create_item;
}


// ─── SUBMIT HOOK ──────────────────────────────────────────────────────────────
function useSubmitSuggestion() {
  const [status,   setStatus]   = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function submit(name, description) {
    setStatus('loading');
    setErrorMsg('');
    try {
      await createItem(name, description);
      setStatus('success');
    } catch (err) {
      console.error('[Suggestions] Submit error:', err.message);
      setErrorMsg(err.message);
      setStatus('error');
    }
  }

  function reset() { setStatus('idle'); setErrorMsg(''); }

  return { status, errorMsg, submit, reset };
}


// ─── ICONS ────────────────────────────────────────────────────────────────────
const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const IconCheck = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconAlert = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const IconBulb = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21h6" />
    <path d="M12 3a6 6 0 0 1 6 6c0 2.22-1.21 4.16-3 5.2V17a1 1 0 0 1-1 1H10a1 1 0 0 1-1-1v-2.8C7.21 13.16 6 11.22 6 9a6 6 0 0 1 6-6z" />
  </svg>
);


// ─── SUCCESS STATE ─────────────────────────────────────────────────────────────
function SuccessState({ onReset }) {
  return (
    <motion.div
      className="sg-success"
      initial={{ opacity: 0, scale: 0.92, y: 16 }}
      animate={{ opacity: 1, scale: 1,    y: 0  }}
      exit={{    opacity: 0, scale: 0.92, y: 16 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="sg-success-icon"><IconCheck /></div>
      <h2 className="sg-success-title">Suggestion submitted!</h2>
      <p className="sg-success-body">
        Thank you for sharing your idea. It's been added to the board and our team will review it soon.
      </p>
      <button className="sg-btn-secondary" onClick={onReset}>Submit another suggestion</button>
    </motion.div>
  );
}


// ─── FORM ─────────────────────────────────────────────────────────────────────
function SuggestionForm({ onSuccess }) {
  const [suggestion,  setSuggestion]  = useState('');
  const [description, setDescription] = useState('');
  const [touched,     setTouched]     = useState({});
  const { status, errorMsg, submit }  = useSubmitSuggestion();

  const suggestionErr =
    touched.suggestion && suggestion.trim().length === 0 ? 'Please enter a suggestion title.' :
    touched.suggestion && suggestion.trim().length < 5   ? 'Please be a little more descriptive (min 5 characters).' :
    null;

  const descriptionErr =
    touched.description && description.trim().length === 0 ? 'Please describe your suggestion.' : null;

  const isLoading = status === 'loading';
  const canSubmit = !isLoading && suggestion.trim().length >= 5 && description.trim().length > 0;

  useEffect(() => {
    if (status === 'success') onSuccess?.();
  }, [status]); // eslint-disable-line

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ suggestion: true, description: true });
    if (!canSubmit) return;
    await submit(suggestion.trim(), description.trim());
  }

  return (
    <motion.form
      className="sg-form"
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0  }}
      exit={{    opacity: 0, y: 24 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      noValidate
    >
      <div className="sg-form-header">
        <div className="sg-form-icon"><IconBulb /></div>
        <div>
          <h1 className="sg-form-title">Share a Suggestion</h1>
          <p className="sg-form-subtitle">Use this form to share your ideas, feedback, or suggestions for improving our intranet. Whether you have an idea for new content, 
            a feature you’d like to see, or something that could be improved, your input helps us make the intranet more useful, accessible, 
            and valuable for everyone.</p>
        </div>
      </div>

      <div className="sg-form-divider" />

      <div className="sg-form-body">

        <div className={`sg-field${suggestionErr ? ' sg-field--error' : ''}`}>
          <label className="sg-label" htmlFor="sg-suggestion">
            Suggestion <span aria-hidden="true">*</span>
          </label>
          <input
            id="sg-suggestion"
            className="sg-input"
            type="text"
            value={suggestion}
            maxLength={120}
            onChange={(e) => setSuggestion(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, suggestion: true }))}
            disabled={isLoading}
            aria-invalid={!!suggestionErr}
          />
          <div className="sg-input-footer">
            {suggestionErr
              ? <span className="sg-field-error" role="alert">{suggestionErr}</span>
              : <span className="sg-char-hint">{suggestion.length}/120</span>
            }
          </div>
        </div>

        <div className={`sg-field${descriptionErr ? ' sg-field--error' : ''}`}>
          <label className="sg-label" htmlFor="sg-description">
            Description <span aria-hidden="true">*</span>
          </label>
          <textarea
            id="sg-description"
            className="sg-textarea"
            value={description}
            rows={5}
            maxLength={1000}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, description: true }))}
            disabled={isLoading}
            aria-invalid={!!descriptionErr}
          />
          <div className="sg-input-footer">
            {descriptionErr
              ? <span className="sg-field-error" role="alert">{descriptionErr}</span>
              : <span className="sg-char-hint">{description.length}/1000</span>
            }
          </div>
        </div>

      </div>

      <AnimatePresence>
        {status === 'error' && (
          <motion.div
            className="sg-api-error"
            role="alert"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0  }}
            exit={{    opacity: 0, y: -8 }}
          >
            <IconAlert /><span>Failed to submit: {errorMsg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="sg-form-footer">
        <button
          type="submit"
          className={`sg-btn-primary${isLoading ? ' sg-btn-primary--loading' : ''}`}
          disabled={!canSubmit}
          aria-busy={isLoading}
        >
          {isLoading
            ? <><span className="sg-btn-spinner" aria-hidden="true" />Submitting…</>
            : <><IconSend />Submit Suggestion</>
          }
        </button>
      </div>
    </motion.form>
  );
}


// ─── PAGE ──────────────────────────────────────────────────────────────────────
const Suggestions = () => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="sg-page">
      <NavBar />

      <div className="sg-banner" style={{ backgroundImage: `url(${SuggestionsBannerImage})` }}>
        <div className="sg-banner-overlay" />
        <div className="sg-banner-text">
          <h2 className="sg-banner-heading">Suggestions</h2>
          <p className="sg-banner-sub">Help Us Build a Better Workplace</p>
        </div>
      </div>

      <div className="sg-page-content">
        <div className="sg-feed">
          <AnimatePresence mode="wait">
            {submitted
              ? <SuccessState key="success" onReset={() => setSubmitted(false)} />
              : <SuggestionForm key="form" onSuccess={() => setSubmitted(true)} />
            }
          </AnimatePresence>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Suggestions;